import makeStyles from "@mui/styles/makeStyles";
import {MouseEventHandler, ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {Node, XYPosition, useNodes, useViewport} from "react-flow-renderer";
import {connect} from "react-redux";
import {selectModeData} from "../../redux/selectors/editor";
import {IGraphData, UpdateModeData, updateModeData} from "../../redux/slices/editor";
import {Transition} from "../../fstn/transition";

const useStyles = makeStyles((theme) => ({
    container: {
        width: "100%",
        height: "100%"
    },
    selectionBox: {
        backgroundColor: "rgba(0,0,255,0.005)",
        position: "absolute",
        border: "dashed 2px rgba(0,0, 255, 1)",
        boxSizing: "border-box",
        borderRadius: "10px",
        zIndex: 1000,
    },
}));

export type OnSelect = (selected: string[]) => void
interface ISelectionRect {
    left: number
    right: number
    top: number
    bottom: number
}
interface INodeData {
    height: number
    width: number
    transitions: Transition[]
}
interface IProps
{
    children: ReactElement | ReactElement[]
    graphData: IGraphData
    updateModeData: UpdateModeData
}
function SelectionBox({children, graphData, updateModeData}: IProps)
{
    const classes = useStyles();
    const nodes = useNodes<INodeData>();
    const {x, y, zoom} = useViewport();
    const [containerBounds, setContainerBounds] = useState<DOMRect>();
    const [pane, setPane] = useState<Element | null>(null);
    const [startPos, setStartPos] = useState<XYPosition>({x: 0, y: 0});
    const [dragging, setDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const selectionBoxRef = useRef<HTMLDivElement>(null);
    const getSelectionRect = useCallback((x: number, y: number): ISelectionRect => {
        return {
            left: Math.min(startPos.x, x) - containerBounds!.x,
            right: Math.max(startPos.x, x) - containerBounds!.x,
            top: Math.min(startPos.y, y) - containerBounds!.y,
            bottom: Math.max(startPos.y, y) - containerBounds!.y,
        }
    }, [containerBounds, startPos]);
    const updateSelectionBox = useCallback((selection: ISelectionRect) =>
    {
        if (selectionBoxRef.current)
        {
            selectionBoxRef.current.style.left = `${selection.left + containerBounds!.x}px`;
            selectionBoxRef.current.style.top = `${selection.top}px`;
            selectionBoxRef.current.style.height = `${selection.bottom - selection.top}px`;
            selectionBoxRef.current.style.width = `${selection.right - selection.left}px`;
        }
    }, [containerBounds]);
    const getClosestNode = (x: number, y: number, nodes: Node<unknown> []) => {
        let closest = Number.MAX_VALUE;

        return nodes.reduce((active: string, current) => {
            const data = current.data as { height: number, width: number };
            const deltaX = current.position.x + data.width * 0.5 - x;
            const deltaY = current.position.y + data.height * 0.5 - y;
            const rangeSquared = deltaX * deltaX + deltaY * deltaY;

            if (rangeSquared < closest) {
                closest = rangeSquared;
                return current.id;
            }
            return active;
        }, "");
    };
    const selectPath = useCallback((id: string) => {
        const path: string[] = [];
        let currentNode = graphData.selectedActive;
        const isCurrentNode = (node: Node) => node.id === currentNode;

        while (currentNode !== id) {
            const node = nodes.find(isCurrentNode);

            if (node === undefined || node.data.transitions.length !== 1 ||
                node.data.transitions[0].destination === undefined) break;
            currentNode =  node.data.transitions[0].destination;
            if (! graphData.selected.includes(currentNode)) {
                path.push(currentNode);
            }
        }
        updateModeData({
            selected: graphData.selected.concat(path)
                .sort((lhs, rhs) => lhs.localeCompare(rhs)),
            selectedActive: id
        });
    }, [graphData, nodes, updateModeData]);
    const handleMouseDown: MouseEventHandler<HTMLElement> = useCallback((event) =>
    {
        // Only interested if the target was the background pane (do not select if clicking on a node)
        if (event.button === 0) {
            if (event.target === pane) {
                setStartPos({x: event.clientX, y: event.clientY});
                setDragging(true);
                event.preventDefault();
            }
        }
    }, [pane]);
    const handleMouseUp: MouseEventHandler<HTMLElement> = useCallback(event =>
    {
        if (dragging) {
            const rect = getSelectionRect(event.clientX, event.clientY);
            const portalRect: ISelectionRect = {
                left: (rect.left - x) / zoom,
                right: (rect.right - x) / zoom,
                top: (rect.top - y) / zoom,
                bottom: (rect.bottom - y) / zoom
            };
            const currentSelection =  nodes.filter(node =>
            {
                if (node.type === "stateNode") {
                    const data = node.data as {height: number, width: number};
                    const nodeRect: ISelectionRect = {
                        left: node.position.x,
                        right: node.position.x + data.width,
                        top: node.position.y,
                        bottom: node.position.y + data.height
                    }

                    return ! (nodeRect.left > portalRect.right || nodeRect.right < portalRect.left ||
                        nodeRect.top > portalRect.bottom || nodeRect.bottom < portalRect.top);
                }
                return false;
            });
            const currentSelectionIds = currentSelection.map(node => node.id)
                .sort((lhs, rhs) => lhs.localeCompare(rhs));
            const selectedActive = getClosestNode((event.clientX - x) / zoom, (event.clientY - y) / zoom,
                currentSelection);

            if (event.metaKey || event.ctrlKey) {
                const selected = Array.from(new Set([...graphData.selected, ...currentSelectionIds]))
                    .sort((lhs, rhs) => lhs.localeCompare(rhs));

                if (selected.join() !== graphData.selected.join()) {
                    updateModeData({
                        selected,
                        selectedActive
                    });
                } else {
                    updateModeData({
                        selectedActive
                    });
                }
            } else if (event.shiftKey && currentSelectionIds.length === 1) {
                selectPath(currentSelectionIds[0]);
            } else {
                if (currentSelection.join() !== graphData.selected.join()) {
                    updateModeData({
                        selected: currentSelectionIds,
                        selectedActive
                    });
                }
            }
        }
        setDragging(false);
    }, [dragging, getSelectionRect, graphData, nodes, selectPath, updateModeData, x, y, zoom]);
    const handleMouseMove: MouseEventHandler<HTMLElement> = useCallback((event) =>
    {
        if (dragging)
        {
            updateSelectionBox(getSelectionRect(event.clientX, event.clientY));
        }
    }, [dragging, getSelectionRect, updateSelectionBox]);

    // Add event listeners to track the pane id, the container size and the select all option
    useEffect(() =>
    {
        if (! containerRef.current) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.key === "a")
            {
                const selectedNodes = nodes.filter(node => node.type === "stateNode");
                const selected = selectedNodes.map(node => node.id).sort((lhs, rhs) =>
                    lhs.localeCompare(rhs));

                if (graphData.selected.join() === selected.join()) {
                    updateModeData({
                        selected: [],
                        selectedActive: ""
                    });
                } else {
                    updateModeData({
                        selected,
                        selectedActive: selected.length ? selected[0] : ""
                    });
                }
                event.preventDefault();
            }
        }
        const handleResize = () => setContainerBounds(containerRef.current!.getBoundingClientRect());

        setPane(containerRef.current.querySelector(".react-flow__pane"));
        handleResize();
        window.addEventListener("resize", handleResize);
        window.addEventListener("keydown", handleKeyDown);
        return () =>
        {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("keydown", handleKeyDown);
        }
    }, [containerRef, graphData.selected, nodes, updateModeData]);
    return (
        <div className={classes.container} ref={containerRef}
             onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
            {dragging && <div ref={selectionBoxRef} className={classes.selectionBox}/>}
            {children}
        </div>
    );
}
const mapStateToProps = (store: never) => ({
    graphData: selectModeData(store) as IGraphData,
});
export default connect(mapStateToProps, {
    updateModeData
})(SelectionBox);
